// frontend/src/components/AlertModal.js
// 既存のAlertModal（レシート番号の緑枠とおつり強調）を維持し、
// 免税レシートのときだけSmartDetax QRを4個ずつ表示します。
import React, { createContext, useContext, useState, useEffect } from 'react';
import '../styles/AlertModal.css';

const AlertContext = createContext();
export const useAlert = () => useContext(AlertContext);

const defaultAlertState = () => ({
  open: false,
  message: '',
  isConfirm: false,
  okText: 'OK',
  cancelText: 'キャンセル',
  showCancel: false,
  className: '',
  smartDetax: null,
});

const QR_PER_PAGE = 4;

export const AlertProvider = ({ children }) => {
  const [alertState, setAlertState] = useState(defaultAlertState);
  const [resolveCallback, setResolveCallback] = useState(null);

  const [qrImages, setQrImages] = useState([]);
  const [qrPage, setQrPage] = useState(0);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState('');
  const [qrPrinting, setQrPrinting] = useState(false);
  // QRを拡大したい場合だけ1個表示。通常は4個ずつ表示。
  const [zoomQrIndex, setZoomQrIndex] = useState(null);

  const showAlert = (message, options = {}) => {
    return new Promise((resolve) => {
      setAlertState({
        open: true,
        message,
        isConfirm: false,
        okText: options.okText || 'OK',
        cancelText: options.cancelText || 'キャンセル',
        showCancel: !!options.showCancel,
        className: options.className || '',
        smartDetax: options.smartDetax || null,
      });
      setResolveCallback(() => resolve);
    });
  };

  const showConfirm = (message, options = {}) => {
    return new Promise((resolve) => {
      setAlertState({
        open: true,
        message,
        isConfirm: true,
        okText: options.okText || 'OK',
        cancelText: options.cancelText || 'キャンセル',
        showCancel: true,
        className: options.className || '',
        smartDetax: null,
      });
      setResolveCallback(() => resolve);
    });
  };

  const close = () => {
    setAlertState(defaultAlertState());
    setResolveCallback(null);
  };

  const handleOk = () => {
    resolveCallback?.(true);
    close();
  };

  const handleCancel = () => {
    resolveCallback?.(false);
    close();
  };

  // RegisterPageの既存コードはcloseAlert()を使っています。
  // 元のProviderには公開されていなかったので、ここで公開します。
  const closeAlert = () => {
    resolveCallback?.(false);
    close();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!alertState.open) return;

      if (e.key === 'Enter') {
        // QRページ切替や印刷ボタンのEnterでOK扱いにしない。
        if (e.target?.closest?.('button')) return;
        handleOk();
      } else if (e.key === 'Escape' && alertState.showCancel) {
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [alertState.open, alertState.showCancel, resolveCallback]);

  // AlertModalを開いた時点でQRを生成（お客様用レシートは印刷しない）。
  useEffect(() => {
    const smart = alertState.open ? alertState.smartDetax : null;
    let alive = true;
    setQrPage(0);
    setQrImages([]);
    setZoomQrIndex(null);
    setQrError(smart?.error || '');
    setQrPrinting(false);
    setQrLoading(Boolean(smart?.loadImages && !smart?.error));

    if (smart?.loadImages && !smart.error) {
      Promise.resolve()
        .then(() => smart.loadImages())
        .then((images) => {
          if (!alive) return;
          if (!Array.isArray(images) || images.length < 1) {
            throw new Error('QRコードの画像がありません');
          }
          setQrImages(images);
          setQrLoading(false);
        })
        .catch((err) => {
          if (!alive) return;
          setQrError(err?.message || 'QR画像の取得に失敗しました');
          setQrLoading(false);
        });
    }
    return () => { alive = false; };
  }, [alertState.open, alertState.smartDetax]);

  const handleSmartDetaxPrint = async () => {
    if (!alertState.smartDetax?.onPrint || qrPrinting || qrLoading || qrError || !qrImages.length) return;
    setQrPrinting(true);
    try {
      const success = await alertState.smartDetax.onPrint();
      if (success === false) {
        setQrError('SmartDetax伝票の印刷に失敗しました。プリンター設定をご確認ください。');
      }
    } catch (err) {
      setQrError(`SmartDetax印刷エラー: ${err?.message || String(err)}`);
    } finally {
      setQrPrinting(false);
    }
  };

  const isReceiptNoAlert =
    alertState.open &&
    alertState.className?.split(/\s+/).includes('AlertModal-highlightChange');

  // 元の「おつり部分だけ暗くしない」動作をそのまま維持
  const [changeHighlightRect, setChangeHighlightRect] = useState(null);
  useEffect(() => {
    if (!isReceiptNoAlert) {
      setChangeHighlightRect(null);
      return;
    }
    const updateRect = () => {
      const target = document.querySelector('.paymentSummary__changeSection');
      if (!target) {
        setChangeHighlightRect(null);
        return;
      }
      const rect = target.getBoundingClientRect();
      setChangeHighlightRect({
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height,
      });
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [isReceiptNoAlert]);

  const qrStart = qrPage * QR_PER_PAGE;
  const shownImages = qrImages.slice(qrStart, qrStart + QR_PER_PAGE);
  const qrPageCount = Math.ceil(qrImages.length / QR_PER_PAGE);

  return (
    <AlertContext.Provider value={{ showAlert, showConfirm, closeAlert }}>
      {children}

      {alertState.open && (
        <div className={`AlertModal-backdrop ${
          isReceiptNoAlert && changeHighlightRect ? 'AlertModal-receiptBackdrop' : ''
        }`}>
          {isReceiptNoAlert && changeHighlightRect && (
            <div className="AlertModal-changeHole" style={changeHighlightRect} aria-hidden="true" />
          )}

          <div
            className={`AlertModal-modal ${alertState.className || ''} ${
              alertState.smartDetax ? 'AlertModal-withSmartDetax' : ''
            }`}
            role="dialog"
            aria-modal="true"
          >
            <div className="AlertModal-message">{alertState.message}</div>

            {alertState.smartDetax && (
              <div className="AlertModal-smartDetax">
                {qrLoading && <div className="AlertModal-smartDetax-status">QRコードを生成中...</div>}

                {qrError && (
                  <div className="AlertModal-smartDetax-error" role="alert">
                    QRエラー: {qrError}<br />
                    <small>売上は保存済みです。再会計しないでください。</small>
                  </div>
                )}

                {qrImages.length > 0 && (
                  <>
                    <div className="AlertModal-smartDetax-caption">
                      SmartDetax用QR　{qrImages.length}個（4個ずつ表示）
                    </div>
                    <div className="AlertModal-smartDetax-grid">
                      {shownImages.map((src, i) => {
                        const qrNumber = qrStart + i + 1;
                        return (
                          <button
                            type="button"
                            className="AlertModal-smartDetax-qrCard"
                            key={qrNumber}
                            onClick={() => setZoomQrIndex(qrNumber - 1)}
                            title="QRを拡大して表示"
                          >
                            <div className="AlertModal-smartDetax-qrLabel">
                              Q{qrNumber} / {qrImages.length}
                            </div>
                            <img src={src} alt={`SmartDetax QR Q${qrNumber}`} />
                          </button>
                        );
                      })}
                    </div>
                    {qrPageCount > 1 && (
                      <div className="AlertModal-smartDetax-pager">
                        <button
                          type="button"
                          className="AlertModal-button"
                          disabled={qrPage === 0}
                          onClick={() => setQrPage((page) => Math.max(0, page - 1))}
                        >前の4個</button>
                        <span>
                          {qrStart + 1}～{Math.min(qrStart + QR_PER_PAGE, qrImages.length)} / {qrImages.length}
                        </span>
                        <button
                          type="button"
                          className="AlertModal-button"
                          disabled={qrPage >= qrPageCount - 1}
                          onClick={() => setQrPage((page) => Math.min(qrPageCount - 1, page + 1))}
                        >次の4個</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {zoomQrIndex !== null && qrImages[zoomQrIndex] && (
              <div className="AlertModal-smartDetax-zoomOverlay" role="presentation">
                <div className="AlertModal-smartDetax-zoomBox" role="dialog" aria-label="QRコード拡大表示">
                  <div>Q{zoomQrIndex + 1} / {qrImages.length}</div>
                  <img src={qrImages[zoomQrIndex]} alt={`SmartDetax QR Q${zoomQrIndex + 1} 拡大`} />
                  <button type="button" className="AlertModal-button" onClick={() => setZoomQrIndex(null)}>
                    4個表示に戻る
                  </button>
                </div>
              </div>
            )}

            <div className="AlertModal-buttonGroup">
              <button type="button" onClick={handleOk} className="AlertModal-button">
                {alertState.okText}
              </button>

              {alertState.smartDetax && (
                <button
                  type="button"
                  onClick={handleSmartDetaxPrint}
                  className="AlertModal-button AlertModal-smartDetax-print"
                  disabled={qrPrinting || qrLoading || Boolean(qrError) || qrImages.length === 0}
                >
                  {qrPrinting ? '印刷中...' : 'SmartDetax印刷'}
                </button>
              )}

              {alertState.showCancel && (
                <button type="button" onClick={handleCancel} className="AlertModal-button">
                  {alertState.cancelText}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </AlertContext.Provider>
  );
};
